import { Injectable, computed, inject, signal } from '@angular/core';
import {
  PatrolDiscordCockpitState,
  PatrolDiscordGuild,
  PatrolCaseRecord,
  PatrolCaseSummary,
  PatrolModerationLedgerEntry,
  PatrolChannelAccessMode,
  PatrolGuildSettings,
  PatrolPolicyPresetId,
  PatrolSetupChecklistItem,
  ageBracketRoleName,
} from '../models/patrol-discord-cockpit.model';
import { PatrolDiscordApiService } from './patrol-discord-api.service';
import { PATROL_KIND_SPEECH_DEFAULTS } from '../constants/patrol-kind-speech-defaults';

export type PatrolDiscordCockpitTab =
  | 'overview'
  | 'policies'
  | 'activity'
  | 'ledger'
  | 'cases'
  | 'queue';

const EMPTY_BOT_HEALTH = {
  ready: false,
  loggedInAs: '',
  inviteUrl: '',
  lastCheckedAt: new Date().toISOString(),
};

const EMPTY_POLICY_STATE: Pick<
  PatrolDiscordCockpitState,
  | 'guildSettings'
  | 'rolePolicies'
  | 'channelPolicies'
  | 'channelAccessPolicies'
  | 'discordRoles'
  | 'discordChannels'
  | 'activityStats'
  | 'recentCases'
  | 'modQueueItems'
> = {
  guildSettings: {},
  rolePolicies: {},
  channelPolicies: {},
  channelAccessPolicies: {},
  discordRoles: {},
  discordChannels: {},
  activityStats: {},
  recentCases: {},
  modQueueItems: {},
};

const DEFAULT_GUILD_SETTINGS: PatrolGuildSettings = {
  defaultPolicyId: 'standard',
  linkRequired: false,
  reputationEnabled: true,
  ageRoleSyncEnabled: false,
  verifiedRoleName: 'Verified',
  ageBrackets: [],
  failClosedOnAiOutage: false,
  notificationsEmail: false,
  ...PATROL_KIND_SPEECH_DEFAULTS,
};

@Injectable({ providedIn: 'root' })
export class PatrolDiscordCockpitService {
  #api = inject(PatrolDiscordApiService);

  readonly #state = signal<PatrolDiscordCockpitState>({
    botHealth: { ...EMPTY_BOT_HEALTH },
    guilds: [],
    ...EMPTY_POLICY_STATE,
  });
  readonly #selectedRegistryId = signal<string>('');
  readonly #activeTab = signal<PatrolDiscordCockpitTab>('overview');
  readonly #isRefreshing = signal(false);
  readonly #isLoadingGuilds = signal(true);
  readonly #isAddingGuild = signal(false);
  readonly #isLoadingActivity = signal(false);
  readonly #activityError = signal<string | null>(null);
  readonly #isLoadingModQueue = signal(false);
  readonly #modQueueError = signal<string | null>(null);
  readonly #modQueueActionId = signal<string | null>(null);
  readonly #isLoadingCases = signal(false);
  readonly #caseRecordError = signal<string | null>(null);
  readonly #caseSummaries = signal<PatrolCaseSummary[]>([]);
  readonly #caseRecordDetail = signal<PatrolCaseRecord | null>(null);
  readonly #copiedCaseExport = signal(false);
  readonly #isLoadingLedger = signal(false);
  readonly #ledgerError = signal<string | null>(null);
  readonly #ledgerItems = signal<PatrolModerationLedgerEntry[]>([]);
  readonly #ledgerNextBefore = signal<string | null>(null);
  readonly #ledgerUserFilter = signal('');
  readonly #guildsError = signal<string | null>(null);
  readonly #copiedInvite = signal(false);
  readonly #isLoadingPolicies = signal(false);
  readonly #isSavingPolicies = signal(false);
  readonly #policiesError = signal<string | null>(null);
  readonly #channelAccessAgeDrafts = signal<
    Record<
      string,
      Record<
        string,
        { minAge: number; maxAge: number; pendingApply?: boolean }
      >
    >
  >({});

  readonly activeTab = this.#activeTab.asReadonly();
  readonly isRefreshing = this.#isRefreshing.asReadonly();
  readonly isLoadingGuilds = this.#isLoadingGuilds.asReadonly();
  readonly isLoadingActivity = this.#isLoadingActivity.asReadonly();
  readonly activityError = this.#activityError.asReadonly();
  readonly isLoadingModQueue = this.#isLoadingModQueue.asReadonly();
  readonly modQueueError = this.#modQueueError.asReadonly();
  readonly modQueueActionId = this.#modQueueActionId.asReadonly();
  readonly isLoadingCases = this.#isLoadingCases.asReadonly();
  readonly caseRecordError = this.#caseRecordError.asReadonly();
  readonly caseSummaries = this.#caseSummaries.asReadonly();
  readonly caseRecordDetail = this.#caseRecordDetail.asReadonly();
  readonly copiedCaseExport = this.#copiedCaseExport.asReadonly();
  readonly isLoadingLedger = this.#isLoadingLedger.asReadonly();
  readonly ledgerError = this.#ledgerError.asReadonly();
  readonly ledgerItems = this.#ledgerItems.asReadonly();
  readonly ledgerNextBefore = this.#ledgerNextBefore.asReadonly();
  readonly ledgerUserFilter = this.#ledgerUserFilter.asReadonly();
  readonly isAddingGuild = this.#isAddingGuild.asReadonly();
  readonly guildsError = this.#guildsError.asReadonly();
  readonly copiedInvite = this.#copiedInvite.asReadonly();
  readonly isLoadingPolicies = this.#isLoadingPolicies.asReadonly();
  readonly isSavingPolicies = this.#isSavingPolicies.asReadonly();
  readonly policiesError = this.#policiesError.asReadonly();

  readonly botHealth = computed(() => this.#state().botHealth);
  readonly guilds = computed(() => this.#state().guilds);
  readonly selectedRegistryId = this.#selectedRegistryId.asReadonly();

  readonly selectedGuild = computed(() =>
    this.guilds().find((g) => g.registryId === this.#selectedRegistryId()),
  );

  readonly isGuildConnected = computed(
    () => this.selectedGuild()?.status === 'active',
  );

  readonly inviteUrlForConnect = computed(() => {
    const guild = this.selectedGuild();
    if (guild?.inviteUrl) {
      return guild.inviteUrl;
    }
    return this.botHealth().inviteUrl;
  });

  readonly selectedContentKey = computed(() => {
    const guild = this.selectedGuild();
    if (!guild) {
      return '';
    }
    return guild.communityId ?? guild.registryId;
  });

  readonly guildSettings = computed(() => {
    const key = this.selectedContentKey();
    return key ? this.#state().guildSettings[key] : undefined;
  });

  readonly rolePolicies = computed(() => {
    const key = this.selectedContentKey();
    return key ? (this.#state().rolePolicies[key] ?? []) : [];
  });

  readonly channelPolicies = computed(() => {
    const key = this.selectedContentKey();
    return key ? (this.#state().channelPolicies[key] ?? []) : [];
  });

  readonly channelAccessPolicies = computed(() => {
    const key = this.selectedContentKey();
    return key ? (this.#state().channelAccessPolicies[key] ?? []) : [];
  });

  readonly discordRoles = computed(() => {
    const key = this.selectedContentKey();
    return key ? (this.#state().discordRoles[key] ?? []) : [];
  });

  readonly discordChannels = computed(() => {
    const key = this.selectedContentKey();
    return key ? (this.#state().discordChannels[key] ?? []) : [];
  });

  readonly activityStats = computed(() => {
    const key = this.selectedContentKey();
    return key ? (this.#state().activityStats[key] ?? []) : [];
  });

  readonly recentCases = computed(() => {
    const key = this.selectedContentKey();
    return key ? (this.#state().recentCases[key] ?? []) : [];
  });

  readonly modQueueItems = computed(() => {
    const key = this.selectedContentKey();
    return key ? (this.#state().modQueueItems[key] ?? []) : [];
  });

  readonly setupChecklist = computed((): PatrolSetupChecklistItem[] => {
    const guild = this.selectedGuild();
    const botReady = this.botHealth().ready;
    const settings = this.guildSettings();
    const roles = this.rolePolicies();
    const channels = this.discordChannels();
    const channelMods = this.channelPolicies();
    const channelAccess = this.channelAccessPolicies();
    const discordRoles = this.discordRoles();
    const checklistI18n = 'app.patrol.discord.cockpit.overview.checklist';

    if (!guild || guild.status !== 'active') {
      return [];
    }

    const moderatedChannelIds = new Set(
      channelMods.map((row) => row.discordChannelId),
    );
    const accessChannelIds = new Set(
      channelAccess.map((row) => row.discordChannelId),
    );
    const nonVoiceChannels = channels.filter(
      (channel) => channel.channelType !== 'voice',
    );
    const allChannelsReviewed =
      nonVoiceChannels.length > 0 &&
      nonVoiceChannels.every(
        (channel) =>
          moderatedChannelIds.has(channel.discordChannelId) ||
          accessChannelIds.has(channel.discordChannelId),
      );

    return [
      {
        id: 'botInstalled',
        done: botReady && !!guild.communityId,
        labelKey: `${checklistI18n}.botInstalled`,
      },
      {
        id: 'guildLinked',
        done: !!guild.communityId && !!guild.linkedAt,
        labelKey: `${checklistI18n}.guildLinked`,
      },
      {
        id: 'defaultPolicy',
        done: !!settings?.defaultPolicyId,
        labelKey: `${checklistI18n}.defaultPolicy`,
      },
      {
        id: 'rolePolicies',
        done: roles.length > 0,
        labelKey: `${checklistI18n}.rolePolicies`,
      },
      {
        id: 'channelPolicies',
        done: allChannelsReviewed,
        labelKey: `${checklistI18n}.channelPolicies`,
      },
      {
        id: 'botSync',
        done:
          botReady && channels.length > 0 && discordRoles.length > 0,
        labelKey: `${checklistI18n}.botSync`,
      },
    ];
  });

  constructor() {
    void this.loadInitialData();
  }

  async loadInitialData(): Promise<void> {
    await Promise.all([this.loadGuilds(), this.loadSetup()]);
  }

  async loadGuilds(): Promise<void> {
    this.#isLoadingGuilds.set(true);
    this.#guildsError.set(null);
    try {
      const guilds = await this.#api.listGuilds();
      this.#state.update((state) => ({ ...state, guilds }));
      this.#ensureSelection(guilds);
      const guild = this.selectedGuild();
      if (guild?.communityId && guild.status === 'active') {
        await this.loadPoliciesForGuild(guild.communityId);
        if (this.#activeTab() === 'activity') {
          await this.loadActivityForGuild(guild.communityId);
        }
        if (this.#activeTab() === 'queue') {
          await this.loadModQueueForGuild(guild.communityId);
        }
      }
    } catch {
      this.#guildsError.set('load_failed');
      this.#state.update((state) => ({ ...state, guilds: [] }));
    } finally {
      this.#isLoadingGuilds.set(false);
    }
  }

  async loadSetup(): Promise<void> {
    try {
      const setup = await this.#api.getSetup();
      this.#state.update((state) => ({
        ...state,
        botHealth: this.#api.mapSetupToBotHealth(setup),
      }));
    } catch {
      this.#state.update((state) => ({
        ...state,
        botHealth: { ...EMPTY_BOT_HEALTH, lastCheckedAt: new Date().toISOString() },
      }));
    }
  }

  async loadPoliciesForGuild(guildId: string): Promise<void> {
    const key = this.selectedContentKey();
    if (!key) {
      return;
    }

    this.#isLoadingPolicies.set(true);
    this.#policiesError.set(null);
    this.#clearChannelAccessAgeDraftsForKey(key);
    try {
      const response = await this.#api.getGuildPolicies(guildId);
      this.#applyPoliciesResponse(key, response);
    } catch {
      this.#policiesError.set('load_failed');
      this.#state.update((state) => ({
        ...state,
        guildSettings: { ...state.guildSettings, [key]: DEFAULT_GUILD_SETTINGS },
        rolePolicies: { ...state.rolePolicies, [key]: [] },
        channelPolicies: { ...state.channelPolicies, [key]: [] },
        channelAccessPolicies: { ...state.channelAccessPolicies, [key]: [] },
        discordRoles: { ...state.discordRoles, [key]: [] },
        discordChannels: { ...state.discordChannels, [key]: [] },
      }));
    } finally {
      this.#isLoadingPolicies.set(false);
    }
  }

  async loadActivityForGuild(guildId: string): Promise<void> {
    const key = this.selectedContentKey();
    if (!key) {
      return;
    }

    this.#isLoadingActivity.set(true);
    this.#activityError.set(null);
    try {
      const response = await this.#api.getGuildActivity(guildId);
      const mapped = this.#api.mapActivityResponse(response);
      this.#state.update((state) => ({
        ...state,
        activityStats: {
          ...state.activityStats,
          [key]: mapped.stats,
        },
        recentCases: {
          ...state.recentCases,
          [key]: mapped.recentCases,
        },
      }));
    } catch {
      this.#activityError.set('load_failed');
      this.#state.update((state) => ({
        ...state,
        activityStats: { ...state.activityStats, [key]: [] },
        recentCases: { ...state.recentCases, [key]: [] },
      }));
    } finally {
      this.#isLoadingActivity.set(false);
    }
  }

  async loadModQueueForGuild(guildId: string): Promise<void> {
    const key = this.selectedContentKey();
    if (!key) {
      return;
    }

    this.#isLoadingModQueue.set(true);
    this.#modQueueError.set(null);
    try {
      const items = await this.#api.listModQueue(guildId);
      this.#state.update((state) => ({
        ...state,
        modQueueItems: {
          ...state.modQueueItems,
          [key]: items,
        },
      }));
    } catch {
      this.#modQueueError.set('load_failed');
      this.#state.update((state) => ({
        ...state,
        modQueueItems: { ...state.modQueueItems, [key]: [] },
      }));
    } finally {
      this.#isLoadingModQueue.set(false);
    }
  }

  async approveModQueueItem(id: string): Promise<boolean> {
    const guildId = this.selectedGuild()?.communityId;
    if (!guildId) {
      return false;
    }

    this.#modQueueError.set(null);
    this.#modQueueActionId.set(id);
    try {
      await this.#api.approveModQueueItem(id);
      await this.loadModQueueForGuild(guildId);
      return true;
    } catch {
      this.#modQueueError.set('action_failed');
      return false;
    } finally {
      this.#modQueueActionId.set(null);
    }
  }

  async rejectModQueueItem(id: string): Promise<boolean> {
    const guildId = this.selectedGuild()?.communityId;
    if (!guildId) {
      return false;
    }

    this.#modQueueError.set(null);
    this.#modQueueActionId.set(id);
    try {
      await this.#api.rejectModQueueItem(id);
      await this.loadModQueueForGuild(guildId);
      return true;
    } catch {
      this.#modQueueError.set('action_failed');
      return false;
    } finally {
      this.#modQueueActionId.set(null);
    }
  }

  async addGuild(displayName: string): Promise<boolean> {
    const trimmed = displayName.trim();
    if (!trimmed) {
      return false;
    }

    this.#isAddingGuild.set(true);
    this.#guildsError.set(null);
    try {
      const created = await this.#api.createGuild(trimmed);
      this.#state.update((state) => ({
        ...state,
        guilds: [...state.guilds, created],
      }));
      this.#selectedRegistryId.set(created.registryId);
      return true;
    } catch {
      this.#guildsError.set('create_failed');
      return false;
    } finally {
      this.#isAddingGuild.set(false);
    }
  }

  selectGuild(registryId: string): void {
    this.#selectedRegistryId.set(registryId);
    this.clearCaseRecordView();
    this.clearLedgerView();
    const guild = this.guilds().find((g) => g.registryId === registryId);
    if (guild?.status !== 'active') {
      this.#activeTab.set('overview');
      return;
    }
    const guildId = guild.communityId;
    if (!guildId) {
      return;
    }
    if (guild.status === 'active') {
      void this.loadPoliciesForGuild(guildId);
    }
    if (this.#activeTab() === 'activity') {
      void this.loadActivityForGuild(guildId);
    }
    if (this.#activeTab() === 'queue') {
      void this.loadModQueueForGuild(guildId);
    }
    if (this.#activeTab() === 'ledger') {
      void this.loadModerationLedger();
    }
  }

  async searchCaseRecords(options: {
    caseIdPrefix?: string;
    discordUserId?: string;
  }): Promise<void> {
    const guildId = this.selectedGuild()?.communityId;
    if (!guildId) {
      return;
    }

    const caseIdPrefix = options.caseIdPrefix?.trim();
    const discordUserId = options.discordUserId?.trim();
    if (!caseIdPrefix && !discordUserId) {
      this.#caseSummaries.set([]);
      this.#caseRecordDetail.set(null);
      return;
    }

    this.#isLoadingCases.set(true);
    this.#caseRecordError.set(null);
    try {
      const summaries = await this.#api.searchCases(guildId, {
        caseIdPrefix,
        discordUserId,
      });
      this.#caseSummaries.set(summaries);
      if (summaries.length === 1) {
        await this.loadCaseRecord(summaries[0].caseId);
      } else {
        this.#caseRecordDetail.set(null);
      }
    } catch {
      this.#caseRecordError.set('search_failed');
      this.#caseSummaries.set([]);
      this.#caseRecordDetail.set(null);
    } finally {
      this.#isLoadingCases.set(false);
    }
  }

  async loadCaseRecord(caseId: string): Promise<void> {
    const guildId = this.selectedGuild()?.communityId;
    if (!guildId || !caseId.trim()) {
      return;
    }

    this.#isLoadingCases.set(true);
    this.#caseRecordError.set(null);
    try {
      const record = await this.#api.getCase(guildId, caseId.trim());
      this.#caseRecordDetail.set(record);
    } catch {
      this.#caseRecordError.set('load_failed');
      this.#caseRecordDetail.set(null);
    } finally {
      this.#isLoadingCases.set(false);
    }
  }

  openCaseRecord(caseId: string): void {
    this.#activeTab.set('cases');
    void this.loadCaseRecord(caseId);
  }

  async copyCaseExport(): Promise<void> {
    const summary = this.#caseRecordDetail()?.exportSummary;
    if (!summary) {
      return;
    }
    try {
      await navigator.clipboard.writeText(summary);
      this.#copiedCaseExport.set(true);
      setTimeout(() => this.#copiedCaseExport.set(false), 2000);
    } catch {
      this.#copiedCaseExport.set(false);
    }
  }

  clearCaseRecordView(): void {
    this.#caseSummaries.set([]);
    this.#caseRecordDetail.set(null);
    this.#caseRecordError.set(null);
  }

  clearLedgerView(): void {
    this.#ledgerItems.set([]);
    this.#ledgerNextBefore.set(null);
    this.#ledgerError.set(null);
    this.#ledgerUserFilter.set('');
  }

  setLedgerUserFilter(value: string): void {
    this.#ledgerUserFilter.set(value);
  }

  async loadModerationLedger(options?: {
    append?: boolean;
    discordUserId?: string;
  }): Promise<void> {
    const guildId = this.selectedGuild()?.communityId;
    if (!guildId) {
      return;
    }

    const append = options?.append === true;
    const discordUserId =
      options?.discordUserId?.trim() ?? this.#ledgerUserFilter().trim();

    if (!append) {
      this.#ledgerItems.set([]);
      this.#ledgerNextBefore.set(null);
    }

    this.#isLoadingLedger.set(true);
    this.#ledgerError.set(null);
    try {
      const response = await this.#api.getModerationLedger(guildId, {
        discordUserId: discordUserId || undefined,
        before: append ? (this.#ledgerNextBefore() ?? undefined) : undefined,
      });
      this.#ledgerItems.update((items) =>
        append ? [...items, ...response.items] : response.items,
      );
      this.#ledgerNextBefore.set(response.nextBefore);
    } catch {
      this.#ledgerError.set('load_failed');
      if (!append) {
        this.#ledgerItems.set([]);
        this.#ledgerNextBefore.set(null);
      }
    } finally {
      this.#isLoadingLedger.set(false);
    }
  }

  async loadMoreModerationLedger(): Promise<void> {
    if (!this.#ledgerNextBefore()) {
      return;
    }
    await this.loadModerationLedger({ append: true });
  }

  setActiveTab(tab: PatrolDiscordCockpitTab): void {
    this.#activeTab.set(tab);
    const guildId = this.selectedGuild()?.communityId;
    if (!guildId) {
      return;
    }
    if (tab === 'activity') {
      void this.loadActivityForGuild(guildId);
    }
    if (tab === 'queue') {
      void this.loadModQueueForGuild(guildId);
    }
    if (tab === 'policies') {
      void this.loadPoliciesForGuild(guildId);
    }
    if (tab === 'cases') {
      this.clearCaseRecordView();
    }
    if (tab === 'ledger') {
      void this.loadModerationLedger();
    }
  }

  async refresh(): Promise<void> {
    this.#isRefreshing.set(true);
    const guildId = this.selectedGuild()?.communityId;
    const tab = this.#activeTab();
    await Promise.all([
      this.loadGuilds(),
      this.loadSetup(),
      tab === 'activity' && guildId
        ? this.loadActivityForGuild(guildId)
        : Promise.resolve(),
      tab === 'queue' && guildId
        ? this.loadModQueueForGuild(guildId)
        : Promise.resolve(),
      tab === 'ledger' && guildId
        ? this.loadModerationLedger()
        : Promise.resolve(),
      guildId && this.selectedGuild()?.status === 'active'
        ? this.loadPoliciesForGuild(guildId)
        : Promise.resolve(),
    ]);
    this.#isRefreshing.set(false);
  }

  openInviteUrl(): void {
    const url = this.inviteUrlForConnect();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  async copyInviteUrl(): Promise<void> {
    const url = this.inviteUrlForConnect();
    if (!url) {
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      this.#copiedInvite.set(true);
      setTimeout(() => this.#copiedInvite.set(false), 2000);
    } catch {
      this.#copiedInvite.set(false);
    }
  }

  rolePolicyForRole(discordRoleId: string): PatrolPolicyPresetId | 'inherit' {
    const row = this.rolePolicies().find((r) => r.discordRoleId === discordRoleId);
    return row?.policyId ?? 'inherit';
  }

  channelPolicyForChannel(
    discordChannelId: string,
  ): PatrolPolicyPresetId | 'inherit' {
    const row = this.channelPolicies().find(
      (c) => c.discordChannelId === discordChannelId,
    );
    return row?.policyId ?? 'inherit';
  }

  async updateGuildSetting<K extends keyof PatrolGuildSettings>(
    key: K,
    value: PatrolGuildSettings[K],
  ): Promise<void> {
    const contentKey = this.selectedContentKey();
    const guildId = this.selectedGuild()?.communityId;
    if (!contentKey || !guildId) {
      return;
    }

    const current = this.guildSettings() ?? DEFAULT_GUILD_SETTINGS;
    const next = { ...current, [key]: value };

    this.#state.update((state) => ({
      ...state,
      guildSettings: {
        ...state.guildSettings,
        [contentKey]: next,
      },
    }));

    await this.#savePolicies(guildId, { guildSettings: next });
  }

  async updateDefaultPolicy(policyId: PatrolPolicyPresetId): Promise<void> {
    await this.updateGuildSetting('defaultPolicyId', policyId);
  }

  channelAccessForChannel(
    discordChannelId: string,
  ): 'inherit' | PatrolChannelAccessMode {
    const draft = this.#channelAccessAgeDraft(discordChannelId);
    if (draft?.pendingApply) {
      return 'age_bracket';
    }

    const row = this.channelAccessPolicies().find(
      (policy) => policy.discordChannelId === discordChannelId,
    );
    return row?.accessMode ?? 'inherit';
  }

  channelAccessAgeForChannel(discordChannelId: string): {
    minAge: number;
    maxAge: number;
    roleName: string;
  } {
    const saved = this.#savedChannelAccessAge(discordChannelId);
    const draft = this.#channelAccessAgeDraft(discordChannelId);
    const minAge = draft?.minAge ?? saved.minAge;
    const maxAge = draft?.maxAge ?? saved.maxAge;
    return {
      minAge,
      maxAge,
      roleName: ageBracketRoleName(minAge, maxAge),
    };
  }

  hasChannelAccessAgeDraft(discordChannelId: string): boolean {
    const draft = this.#channelAccessAgeDraft(discordChannelId);
    if (!draft) {
      return false;
    }
    if (draft.pendingApply) {
      return true;
    }
    const saved = this.#savedChannelAccessAge(discordChannelId);
    return draft.minAge !== saved.minAge || draft.maxAge !== saved.maxAge;
  }

  stageChannelAccessAgeBracket(discordChannelId: string): void {
    const key = this.selectedContentKey();
    if (!key) {
      return;
    }

    const row = this.channelAccessPolicies().find(
      (policy) => policy.discordChannelId === discordChannelId,
    );
    if (row?.accessMode === 'age_bracket') {
      return;
    }

    const defaults = this.#savedChannelAccessAge(discordChannelId);
    this.#channelAccessAgeDrafts.update((drafts) => ({
      ...drafts,
      [key]: {
        ...drafts[key],
        [discordChannelId]: { ...defaults, pendingApply: true },
      },
    }));
  }

  setChannelAccessAgeDraft(
    discordChannelId: string,
    field: 'minAge' | 'maxAge',
    value: string | number,
  ): void {
    const key = this.selectedContentKey();
    if (!key) {
      return;
    }

    const saved = this.#savedChannelAccessAge(discordChannelId);
    const current = this.#channelAccessAgeDraft(discordChannelId) ?? saved;
    const next = {
      ...current,
      [field]: Number(value),
    };

    this.#channelAccessAgeDrafts.update((drafts) => ({
      ...drafts,
      [key]: {
        ...drafts[key],
        [discordChannelId]: next,
      },
    }));
  }

  async confirmChannelAccessAge(discordChannelId: string): Promise<void> {
    const draft = this.#channelAccessAgeDraft(discordChannelId);
    if (!draft || !this.hasChannelAccessAgeDraft(discordChannelId)) {
      return;
    }

    await this.updateChannelAccess(discordChannelId, 'age_bracket', {
      minAge: draft.minAge,
      maxAge: draft.maxAge,
    });
  }

  async updateChannelAccess(
    discordChannelId: string,
    accessMode: 'inherit' | PatrolChannelAccessMode,
    age?: { minAge: number; maxAge: number },
  ): Promise<void> {
    const guildId = this.selectedGuild()?.communityId;
    const contentKey = this.selectedContentKey();
    if (!guildId || !contentKey) {
      return;
    }

    if (accessMode !== 'age_bracket') {
      this.#clearChannelAccessAgeDraft(discordChannelId);
    }

    const channel = this.discordChannels().find(
      (c) => c.discordChannelId === discordChannelId,
    );
    if (!channel) {
      return;
    }

    const existing = this.channelAccessPolicies().filter(
      (row) => row.discordChannelId !== discordChannelId,
    );

    const nextRows =
      accessMode === 'inherit'
        ? existing
        : [
            ...existing,
            {
              id: discordChannelId,
              discordChannelId,
              channelName: channel.channelName,
              channelType: channel.channelType,
              accessMode,
              minAge:
                accessMode === 'age_bracket' ? (age?.minAge ?? 10) : undefined,
              maxAge:
                accessMode === 'age_bracket' ? (age?.maxAge ?? 15) : undefined,
              roleName:
                accessMode === 'age_bracket'
                  ? ageBracketRoleName(age?.minAge ?? 10, age?.maxAge ?? 15)
                  : undefined,
            },
          ];

    this.#state.update((state) => ({
      ...state,
      channelAccessPolicies: {
        ...state.channelAccessPolicies,
        [contentKey]: nextRows,
      },
    }));

    await this.#savePolicies(guildId, {
      channelAccessPolicies: nextRows.map((row) => ({
        discordChannelId: row.discordChannelId,
        accessMode: row.accessMode,
        minAge: row.minAge,
        maxAge: row.maxAge,
        roleName: row.roleName,
      })),
    });

    if (accessMode === 'age_bracket') {
      this.#clearChannelAccessAgeDraft(discordChannelId);
      await this.refetchPoliciesForGuild(guildId);
    }
  }

  async refetchPoliciesForGuild(guildId: string): Promise<void> {
    const key = this.selectedContentKey();
    if (!key) {
      return;
    }

    try {
      const response = await this.#api.getGuildPolicies(guildId);
      this.#applyPoliciesResponse(key, response);
    } catch {
      // Keep current state if the refresh fails after a successful save.
    }
  }

  #savedChannelAccessAge(discordChannelId: string): {
    minAge: number;
    maxAge: number;
  } {
    const row = this.channelAccessPolicies().find(
      (policy) => policy.discordChannelId === discordChannelId,
    );
    return {
      minAge: row?.minAge ?? 10,
      maxAge: row?.maxAge ?? 15,
    };
  }

  #channelAccessAgeDraft(
    discordChannelId: string,
  ): { minAge: number; maxAge: number; pendingApply?: boolean } | undefined {
    const key = this.selectedContentKey();
    if (!key) {
      return undefined;
    }
    return this.#channelAccessAgeDrafts()[key]?.[discordChannelId];
  }

  #clearChannelAccessAgeDraft(discordChannelId: string): void {
    const key = this.selectedContentKey();
    if (!key) {
      return;
    }

    this.#channelAccessAgeDrafts.update((drafts) => {
      const perGuild = { ...drafts[key] };
      delete perGuild[discordChannelId];
      return { ...drafts, [key]: perGuild };
    });
  }

  #clearChannelAccessAgeDraftsForKey(key: string): void {
    this.#channelAccessAgeDrafts.update((drafts) => {
      if (!drafts[key]) {
        return drafts;
      }
      const next = { ...drafts };
      delete next[key];
      return next;
    });
  }

  async updateRolePolicy(
    discordRoleId: string,
    policyId: PatrolPolicyPresetId | 'inherit',
  ): Promise<void> {
    const guildId = this.selectedGuild()?.communityId;
    const contentKey = this.selectedContentKey();
    if (!guildId || !contentKey) {
      return;
    }

    const role = this.discordRoles().find((r) => r.discordRoleId === discordRoleId);
    if (!role) {
      return;
    }

    const existing = this.rolePolicies().filter(
      (row) => row.discordRoleId !== discordRoleId,
    );
    const nextRows =
      policyId === 'inherit'
        ? existing
        : [
            ...existing,
            {
              id: discordRoleId,
              discordRoleId,
              roleName: role.roleName,
              roleColor: role.roleColor,
              policyId,
              memberCount: role.memberCount,
            },
          ];

    this.#state.update((state) => ({
      ...state,
      rolePolicies: { ...state.rolePolicies, [contentKey]: nextRows },
    }));

    await this.#savePolicies(guildId, {
      rolePolicies: nextRows.map((row) => ({
        discordRoleId: row.discordRoleId,
        policyId: row.policyId,
      })),
    });
  }

  async updateChannelPolicy(
    discordChannelId: string,
    policyId: PatrolPolicyPresetId | 'inherit',
  ): Promise<void> {
    const guildId = this.selectedGuild()?.communityId;
    const contentKey = this.selectedContentKey();
    if (!guildId || !contentKey) {
      return;
    }

    const channel = this.discordChannels().find(
      (c) => c.discordChannelId === discordChannelId,
    );
    if (!channel) {
      return;
    }

    const existing = this.channelPolicies().filter(
      (row) => row.discordChannelId !== discordChannelId,
    );
    const nextRows =
      policyId === 'inherit'
        ? existing
        : [
            ...existing,
            {
              id: discordChannelId,
              discordChannelId,
              channelName: channel.channelName,
              channelType: channel.channelType,
              policyId,
            },
          ];

    this.#state.update((state) => ({
      ...state,
      channelPolicies: { ...state.channelPolicies, [contentKey]: nextRows },
    }));

    await this.#savePolicies(guildId, {
      channelPolicies: nextRows.map((row) => ({
        discordChannelId: row.discordChannelId,
        policyId: row.policyId,
      })),
    });
  }

  async #savePolicies(
    guildId: string,
    body: Parameters<PatrolDiscordApiService['updateGuildPolicies']>[1],
  ): Promise<void> {
    const key = this.selectedContentKey();
    if (!key) {
      return;
    }

    this.#isSavingPolicies.set(true);
    this.#policiesError.set(null);
    try {
      const response = await this.#api.updateGuildPolicies(guildId, body);
      this.#applyPoliciesResponse(key, response);
    } catch {
      this.#policiesError.set('save_failed');
    } finally {
      this.#isSavingPolicies.set(false);
    }
  }

  #applyPoliciesResponse(
    key: string,
    response: Awaited<ReturnType<PatrolDiscordApiService['getGuildPolicies']>>,
  ): void {
    this.#state.update((state) => ({
      ...state,
      guildSettings: {
        ...state.guildSettings,
        [key]: response.guildSettings,
      },
      rolePolicies: {
        ...state.rolePolicies,
        [key]: response.rolePolicies,
      },
      channelPolicies: {
        ...state.channelPolicies,
        [key]: response.channelPolicies,
      },
      channelAccessPolicies: {
        ...state.channelAccessPolicies,
        [key]: response.channelAccessPolicies ?? [],
      },
      discordRoles: {
        ...state.discordRoles,
        [key]: response.discordRoles,
      },
      discordChannels: {
        ...state.discordChannels,
        [key]: response.discordChannels,
      },
    }));
  }

  formatDate(iso: string | undefined | null): string {
    if (!iso) {
      return '—';
    }
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  }

  #ensureSelection(guilds: PatrolDiscordGuild[]): void {
    if (guilds.length === 0) {
      this.#selectedRegistryId.set('');
      return;
    }
    const current = this.#selectedRegistryId();
    if (current && guilds.some((g) => g.registryId === current)) {
      return;
    }
    const preferred =
      guilds.find((g) => g.status === 'active') ?? guilds[0];
    this.#selectedRegistryId.set(preferred.registryId);
  }
}
